import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import React from "react";
import { BASE_API_URL } from "store/Me";
import moment from "moment";
import { GuestType } from "./AdminGuests";
import type { OnboardGroupInfo } from "api/ApiClient";
import type { PropertyStatus } from "components/Properties/hooks/usePropertyData";
import { statusBadgeVariant } from "shared/utils/statusChipColor";
import { Badge } from "components/ui/badge";
import { AdminTable } from "components/shared/AdminTable";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";

export default function GuestPropertyAccess({ guestId }: { guestId: string }) {
  const { data, isError } = useGetGuestPropertyAccess(guestId);

  if (isError) {
    return <div>There was an error loading property access.</div>;
  }

  if (data) {
    return (
      <div>
        <AdminTable aria-label="Guest property access" containerClassName="mt-1">
          <thead>
            <tr>
              <th scope="col">Property</th>
              <th scope="col">Unit Number</th>
              <th scope="col">Lease Id</th>
              <th scope="col">Guest Type</th>
              <th scope="col">Is Guest</th>
              <th scope="col">Additional Entryways</th>
              <th scope="col">Valid From</th>
              <th scope="col">Valid To</th>
              <th scope="col">Deleted</th>
            </tr>
          </thead>
          <tbody>
              {data.map((propertyAccess) => (
                <tr key={propertyAccess.id}>
                  <td>
                    <div className="flex items-center justify-between gap-1">
                      <span>{propertyAccess.propertyName}</span>
                      {propertyAccess.propertyStatus &&
                        propertyAccess.propertyStatus !== "Active" && (
                          <Badge
                            className="h-4 px-1.5 text-[10px]"
                            variant={statusBadgeVariant(
                              propertyAccess.propertyStatus,
                            )}
                          >
                            {propertyAccess.propertyStatus}
                          </Badge>
                        )}
                    </div>
                    <div style={{ fontSize: 11, color: "gray" }}>
                      {propertyAccess.propertyExternalId}
                    </div>
                  </td>
                  <td>{propertyAccess.unitNumber}</td>
                  <td>{propertyAccess.leaseId}</td>
                  <td>{propertyAccess.guestType}</td>
                  <td>{propertyAccess.isGuest ? "Yes" : "No"}</td>
                  <td>
                    <AdditionalEntrywaysCell groups={propertyAccess.groups} />
                  </td>
                  <td>
                    {propertyAccess.validFrom
                      ? moment(propertyAccess.validFrom).format(
                          "YYYY-MM-DD h:mm A",
                        )
                      : ""}
                  </td>
                  <td>
                    {propertyAccess.validTo
                      ? moment(propertyAccess.validTo).format(
                          "YYYY-MM-DD h:mm A",
                        )
                      : ""}
                  </td>
                  <td>
                    {propertyAccess.deletedTimestamp
                      ? moment(propertyAccess.deletedTimestamp).format(
                          "YYYY-MM-DD h:mm A",
                        )
                      : ""}
                  </td>
                </tr>
              ))}
          </tbody>
        </AdminTable>
      </div>
    );
  }

  return <div>loading...</div>;
}

function AdditionalEntrywaysCell({ groups }: { groups: OnboardGroupInfo[] }) {
  if (!groups || groups.length === 0) {
    return <span style={{ color: "gray" }}>--</span>;
  }

  const names = groups.map((group) => group.name?.trim() || group.groupUniqueName);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0}>
          <Badge variant="outline">
            {groups.length} {groups.length === 1 ? "entryway" : "entryways"}
          </Badge>
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <div>
          {names.map((name, index) => (
            <div key={`${name}-${index}`}>{name}</div>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

export interface GuestPropertyAccessResponse {
  id: string;
  propertyExternalId: string;
  propertyName: string | null;
  propertyStatus?: PropertyStatus | null;
  guestType: GuestType;
  leaseId: string | null;
  unitNumber: string | null;
  isGuest: boolean;
  validFrom: string | null;
  validTo: string | null;
  createdTimestamp: string;
  deletedTimestamp: string | null;
  /** Additional entryway access control groups granted beyond this unit/property's default access. */
  groups: OnboardGroupInfo[];
}

async function getGuestPropertyAccess(guestId: string) {
  const url = `${BASE_API_URL}/api/guest/${guestId}/property-access`;
  const response = await axios.get<GuestPropertyAccessResponse[]>(url);

  return response.data;
}

export function useGetGuestPropertyAccess(guestId: string) {
  return useQuery({
    queryKey: ["guests", "property-access", guestId],
    queryFn: () => getGuestPropertyAccess(guestId),
  });
}
